import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  TablePagination,
  Box,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';

function GestionEstadosTomaMuestra() {
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;
  const { user } = useUser();

  // Función para obtener el usuario actual con fallback
  const getCurrentUser = () => {
    return user?.usuario || user?.codigoUsuario || 'ADMIN';
  };

  // Función para obtener headers de autenticación
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Agregar API key si está disponible
    if (key) {
      headers['x-api-key'] = key;
    }
    
    // Agregar token de autorización si está disponible
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Debug: Mostrar headers en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log('Headers de autenticación:', {
        hasApiKey: !!key,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 10)}...` : 'N/A'
      });
    }
    
    return headers;
  };

  // Función para manejar errores de autenticación de forma consistente
  const handleAuthError = (error) => {
    console.error('Error de autenticación:', error);
    
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      
      switch (status) {
        case 401:
          setSnackbar({
            open: true,
            message: 'Error de autenticación: Token expirado o inválido. Por favor, inicie sesión nuevamente.',
            severity: 'error'
          });
          // Opcional: redirigir al login
          // window.location.href = '/login';
          break;
        case 403:
          setSnackbar({
            open: true,
            message: 'No tiene permisos para realizar esta acción.',
            severity: 'error'
          });
          break;
        case 500:
          setSnackbar({
            open: true,
            message: 'Error interno del servidor. Por favor, contacte al administrador.',
            severity: 'error'
          });
          break;
        default:
          setSnackbar({
            open: true,
            message: `Error ${status}: ${message}`,
            severity: 'error'
          });
      }
    } else if (error.request) {
      setSnackbar({
        open: true,
        message: 'Error de conexión: No se pudo conectar con el servidor.',
        severity: 'error'
      });
    } else {
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Estados principales
  const [estados, setEstados] = useState([]);
  const [filteredEstados, setFilteredEstados] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  // Estados para el formulario
  const [openForm, setOpenForm] = useState(false);
  const [editingEstado, setEditingEstado] = useState(null);
  const [formData, setFormData] = useState({
    estado: ''
  });

  // Estados para el modal de confirmación de eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [estadoToDelete, setEstadoToDelete] = useState(null);

  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Función para normalizar órdenes para que sean secuenciales (1, 2, 3, 4...)
  const normalizeOrders = async (estadosData) => {
    const usuario = getCurrentUser();
    if (!usuario) return estadosData;

    let needsNormalization = false;
    
    // Verificar si los órdenes necesitan normalización
    for (let i = 0; i < estadosData.length; i++) {
      const expectedOrder = i + 1;
      if (estadosData[i].orden !== expectedOrder) {
        needsNormalization = true;
        break;
      }
    }

    if (!needsNormalization) {
      return estadosData;
    }

    console.log('Normalizando órdenes de estados...');
    
    try {
      // Actualizar cada estado con su orden correcto
      for (let i = 0; i < estadosData.length; i++) {
        const estado = estadosData[i];
        const correctOrder = i + 1;
        
        if (estado.orden !== correctOrder) {
          await updateOrdenEstado(estado.id, correctOrder);
          estado.orden = correctOrder; // Actualizar localmente
        }
      }
      
      console.log('Órdenes normalizados correctamente');
      return estadosData;
    } catch (error) {
      console.error('Error al normalizar órdenes:', error);
      return estadosData;
    }
  };

  // Función para cargar estados desde la API
  const fetchEstados = async () => {
    setLoading(true);
    try {
      // Verificar que tenemos token de autenticación
      const token = localStorage.getItem('token');
      if (!token) {
        setSnackbar({
          open: true,
          message: 'No se encontró token de autenticación. Por favor, inicie sesión.',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      const response = await axios.get(`${server}/mg_estados_toma_muestras`, {
        headers: getAuthHeaders()
      });
      // Manejar la estructura de respuesta con $values
      const estadosData = response.data.$values || response.data;
      
      // Ordenar: primero los que tienen orden (por valor de orden), luego los que no tienen (por ID)
      const estadosOrdenados = estadosData.sort((a, b) => {
        // Primero verificar si alguno no tiene orden (undefined o null)
        const aHasOrder = a.orden !== undefined && a.orden !== null;
        const bHasOrder = b.orden !== undefined && b.orden !== null;
        
        // Si uno tiene orden y el otro no, el que tiene orden va primero
        if (aHasOrder && !bHasOrder) return -1;
        if (!aHasOrder && bHasOrder) return 1;
        
        // Si ambos tienen orden, comparar por valor de orden
        if (aHasOrder && bHasOrder) {
          return a.orden - b.orden;
        }
        
        // Si ninguno tiene orden, comparar por ID (ambos van al final)
        return a.id - b.id;
      });
      
      // Normalizar órdenes para que sean secuenciales
      const estadosNormalizados = await normalizeOrders(estadosOrdenados);
      
      setEstados(estadosNormalizados);
      setFilteredEstados(estadosNormalizados);
    } catch (error) {
      console.error('Error al cargar estados:', error);
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect para cargar datos iniciales
  useEffect(() => {
    fetchEstados();
  }, []);

  // Filtrar estados basado en la búsqueda
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredEstados(estados);
    } else {
      const filtered = estados.filter(estado =>
        estado.estado.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEstados(filtered);
    }
    setPage(0); // Reset page when searching
  }, [searchQuery, estados]);

  // Función para actualizar orden de estado
  const updateOrdenEstado = async (idEstado, nuevoOrden) => {
    try {
      const usuario = getCurrentUser();
      await axios.put(
        `${server}/mg_estados_toma_muestras/ordenar/${idEstado}/${usuario}/${nuevoOrden}`,
        {},
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error('Error al actualizar orden:', error);
      throw error;
    }
  };

  // Función para mover estado hacia arriba
  const handleMoveUpEstado = async (index) => {
    if (index === 0) return;

    const currentEstado = filteredEstados[index];
    const previousEstado = filteredEstados[index - 1];

    console.log('Intercambiando órdenes de estados:', {
      currentEstado: {
        id: currentEstado.id,
        estado: currentEstado.estado,
        ordenActual: currentEstado.orden,
        posicionActual: index + 1,
        nuevaPosicion: index
      },
      previousEstado: {
        id: previousEstado.id,
        estado: previousEstado.estado,
        ordenActual: previousEstado.orden,
        posicionActual: index,
        nuevaPosicion: index + 1
      }
    });

    try {
      const usuario = getCurrentUser();
      
      // Usar la posición como orden (1, 2, 3, 4...)
      const newCurrentOrden = index; // Nueva posición del elemento actual (sube)
      const newPreviousOrden = index + 1; // Nueva posición del elemento anterior (baja)
      
      await updateOrdenEstado(currentEstado.id, newCurrentOrden);
      await updateOrdenEstado(previousEstado.id, newPreviousOrden);

      console.log('Órdenes de estados actualizados correctamente');
      await fetchEstados();
      setSnackbar({
        open: true,
        message: 'Orden actualizado correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al mover estado hacia arriba:', error);
      handleAuthError(error);
    }
  };

  // Función para mover estado hacia abajo
  const handleMoveDownEstado = async (index) => {
    if (index === filteredEstados.length - 1) return;

    const currentEstado = filteredEstados[index];
    const nextEstado = filteredEstados[index + 1];

    console.log('Intercambiando órdenes de estados:', {
      currentEstado: {
        id: currentEstado.id,
        estado: currentEstado.estado,
        ordenActual: currentEstado.orden,
        posicionActual: index + 1,
        nuevaPosicion: index + 2
      },
      nextEstado: {
        id: nextEstado.id,
        estado: nextEstado.estado,
        ordenActual: nextEstado.orden,
        posicionActual: index + 2,
        nuevaPosicion: index + 1
      }
    });

    try {
      const usuario = getCurrentUser();
      
      // Usar la posición como orden (1, 2, 3, 4...)
      const newCurrentOrden = index + 2; // Nueva posición del elemento actual (baja)
      const newNextOrden = index + 1; // Nueva posición del elemento siguiente (sube)
      
      await updateOrdenEstado(currentEstado.id, newCurrentOrden);
      await updateOrdenEstado(nextEstado.id, newNextOrden);

      console.log('Órdenes de estados actualizados correctamente');
      await fetchEstados();
      setSnackbar({
        open: true,
        message: 'Orden actualizado correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al mover estado hacia abajo:', error);
      handleAuthError(error);
    }
  };

  // Handlers para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handlers para el formulario
  const handleOpenForm = () => {
    setFormData({ estado: '' });
    setEditingEstado(null);
    setOpenForm(true);
  };

  const handleEditEstado = (estado) => {
    setFormData({
      estado: estado.estado
    });
    setEditingEstado(estado);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingEstado(null);
    setFormData({ estado: '' });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitForm = async () => {
    if (!formData.estado.trim()) {
      setSnackbar({
        open: true,
        message: 'El nombre del estado es requerido',
        severity: 'warning'
      });
      return;
    }

    // Validar que el usuario esté disponible
    const usuario = getCurrentUser();
    if (!usuario) {
      setSnackbar({
        open: true,
        message: 'Error: Usuario no identificado. Por favor, inicie sesión nuevamente.',
        severity: 'error'
      });
      return;
    }

    try {
      const estadoData = {
        estado: formData.estado.trim()
      };

      if (editingEstado) {
        // Al editar, mantenemos todos los campos originales y solo modificamos lo necesario
        estadoData.id = editingEstado.id;
        estadoData.modificadoPor = usuario;
        estadoData.fechaModificacion = new Date().toISOString();
        estadoData.modificacion = editingEstado.modificacion || 1;
        estadoData.orden = editingEstado.orden;
        estadoData.ingresadoPor = editingEstado.ingresadoPor;
        estadoData.fechaIngreso = editingEstado.fechaIngreso;
        
        await axios.put(`${server}/mg_estados_toma_muestras/${editingEstado.id}`, estadoData, {
          headers: getAuthHeaders()
        });
        setSnackbar({
          open: true,
          message: 'Estado actualizado exitosamente',
          severity: 'success'
        });
      } else {
        // Al insertar, solo enviamos los campos necesarios para creación
        estadoData.ingresadoPor = usuario;
        estadoData.fechaIngreso = new Date().toISOString();
        estadoData.modificacion = 1;
        
        // El nuevo estado siempre va al final de la secuencia
        estadoData.orden = estados.length + 1;
        
        await axios.post(`${server}/mg_estados_toma_muestras`, estadoData, {
          headers: getAuthHeaders()
        });
        setSnackbar({
          open: true,
          message: 'Estado creado exitosamente',
          severity: 'success'
        });
      }

      handleCloseForm();
      fetchEstados();
    } catch (error) {
      console.error('Error al guardar estado:', error);
      handleAuthError(error);
    }
  };

  // Handlers para eliminación
  const handleOpenDeleteDialog = (estado) => {
    setEstadoToDelete(estado);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setEstadoToDelete(null);
  };

  const handleDeleteEstado = async () => {
    if (!estadoToDelete) return;

    const usuario = getCurrentUser();
    if (!usuario) {
      setSnackbar({
        open: true,
        message: 'Error: Usuario no identificado. Por favor, inicie sesión nuevamente.',
        severity: 'error'
      });
      return;
    }

    try {
      await axios.delete(`${server}/mg_estados_toma_muestras/${estadoToDelete.id}/${usuario}`, {
        headers: getAuthHeaders()
      });
      setSnackbar({
        open: true,
        message: 'Estado eliminado exitosamente',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      fetchEstados();
    } catch (error) {
      console.error('Error al eliminar estado:', error);
      handleAuthError(error);
    }
  };

  // Datos para mostrar en la tabla actual
  const paginatedEstados = filteredEstados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>Gestión de Estados de Toma de Muestra</h3>
          </div>
        </div>

        <div className="clearfix"></div>

        <div className="row">
          <div className="col-md-12 col-sm-12 col-xs-12">
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" component="h2">
                    Estados de Toma de Muestra
                    {process.env.NODE_ENV === 'development' && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        Usuario: {getCurrentUser()} | Total: {estados.length}
                      </Typography>
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenForm}
                    disabled={!getCurrentUser() || !tienePermiso('Crear Estado Toma Muestra')}
                  >
                    Nuevo Estado
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar estado..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ marginBottom: '20px' }}
                />

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Orden</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Fecha Ingreso</TableCell>
                        <TableCell>Ingresado Por</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Cargando...
                          </TableCell>
                        </TableRow>
                      ) : paginatedEstados.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No se encontraron estados
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedEstados.map((estado, index) => {
                          const globalIndex = page * rowsPerPage + index;
                          const posicionReal = globalIndex + 1; // Posición real en la lista (1, 2, 3, 4...)
                          return (
                            <TableRow key={estado.id}>
                              <TableCell>
                                <Chip 
                                  label={posicionReal}
                                  size="small" 
                                  color={estado.orden !== null && estado.orden !== undefined ? "primary" : "default"}
                                  variant={estado.orden !== null && estado.orden !== undefined ? "filled" : "outlined"}
                                />
                              </TableCell>
                              <TableCell>{estado.estado}</TableCell>
                              <TableCell>
                                {estado.fechaIngreso 
                                  ? new Date(estado.fechaIngreso).toLocaleDateString()
                                  : 'N/A'
                                }
                              </TableCell>
                              <TableCell>{estado.ingresadoPor?.trim()}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveUpEstado(globalIndex)}
                                  disabled={globalIndex === 0}
                                  title="Mover hacia arriba"
                                >
                                  <ArrowUpIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveDownEstado(globalIndex)}
                                  disabled={globalIndex === filteredEstados.length - 1}
                                  title="Mover hacia abajo"
                                >
                                  <ArrowDownIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditEstado(estado)}
                                  title="Editar"
                                  disabled={!tienePermiso('Editar Estado Toma Muestra')}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(estado)}
                                  title="Eliminar"
                                  color="error"
                                  disabled={!tienePermiso('Eliminar Estado Toma Muestra')}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredEstados.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página:"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal de formulario */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEstado ? 'Editar Estado' : 'Nuevo Estado'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nombre del Estado"
            variant="outlined"
            value={formData.estado}
            onChange={(e) => handleFormChange('estado', e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>
            Cancelar
          </Button>
          <Button onClick={handleSubmitForm} variant="contained" color="primary">
            {editingEstado ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar el estado "{estadoToDelete?.estado}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteEstado} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default GestionEstadosTomaMuestra;
